import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { toast } from "sonner";

interface AddOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  supplierId: string;
}

export const AddOrderDialog = ({
  open,
  onOpenChange,
  onSuccess,
  supplierId,
}: AddOrderDialogProps) => {
  const [formData, setFormData] = useState({
    order_number: "",
    total_amount: "",
    payment_terms: "",
    expected_delivery_date: "",
    notes: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      toast.error("Vous devez être connecté pour ajouter une commande");
      return;
    }

    // Format the date to ISO string if it exists
    const expected_delivery_date = formData.expected_delivery_date 
      ? new Date(formData.expected_delivery_date).toISOString()
      : null;

    const { error } = await supabase
      .from("supplier_orders")
      .insert([{
        ...formData,
        supplier_id: supplierId,
        owner_id: user.id,
        total_amount: parseFloat(formData.total_amount),
        status: "pending",
        expected_delivery_date
      }]);

    if (error) {
      console.error("Error adding order:", error);
      toast.error("Erreur lors de l'ajout de la commande");
      return;
    }

    toast.success("Commande ajoutée avec succès");
    onSuccess();
    onOpenChange(false);
    setFormData({
      order_number: "",
      total_amount: "",
      payment_terms: "",
      expected_delivery_date: "",
      notes: "",
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nouvelle commande</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="order_number">Numéro de commande*</Label>
            <Input
              id="order_number"
              value={formData.order_number}
              onChange={(e) =>
                setFormData({ ...formData, order_number: e.target.value })
              }
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="total_amount">Montant total*</Label>
            <Input
              id="total_amount"
              type="number"
              value={formData.total_amount}
              onChange={(e) =>
                setFormData({ ...formData, total_amount: e.target.value })
              }
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="payment_terms">Conditions de paiement</Label>
            <Input
              id="payment_terms"
              value={formData.payment_terms}
              onChange={(e) =>
                setFormData({ ...formData, payment_terms: e.target.value })
              }
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="expected_delivery_date">Date de livraison prévue</Label>
            <Input
              id="expected_delivery_date"
              type="date"
              value={formData.expected_delivery_date}
              onChange={(e) =>
                setFormData({ ...formData, expected_delivery_date: e.target.value })
              }
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit">Ajouter</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};